/**
 * Custom hook for country autocomplete
 * 
 * Uses restcountries.com API with client-side debounced filtering
 */
import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useDebouncedValue } from '../../../hooks/useDebouncedValue'

export interface CountrySuggestion {
  value: string
  label: string
}

interface RestCountry {
  name: {
    common: string
  }
}

/**
 * Fetches all countries from restcountries.com API
 */
const fetchAllCountries = async (): Promise<CountrySuggestion[]> => {
  const response = await fetch('https://restcountries.com/v3.1/all?fields=name')
  if (!response.ok) {
    throw new Error('Failed to fetch countries')
  }
  const countries: RestCountry[] = await response.json()
  
  return countries
    .map(country => ({
      value: country.name.common,
      label: country.name.common,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

/**
 * Hook for country autocomplete with debounced filtering
 * 
 * @param options - Configuration options
 * @returns Autocomplete hook result with filtered suggestions and loading state
 * 
 * @example
 * ```tsx
 * const { suggestions, isLoading, updateQuery } = useCountryAutocomplete()
 * 
 * <input onChange={(e) => updateQuery(e.target.value)} />
 * {suggestions.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
 * ```
 */
export function useCountryAutocomplete(
  options: {
    enabled?: boolean
  } = {}
) {
  const { enabled = true } = options
  const [query, setQuery] = useState('')
  
  // Debounce the query for filtering
  const debouncedQuery = useDebouncedValue(query, 300)

  // Fetch all countries once and cache them
  const { data: allCountries, isLoading, error } = useQuery<CountrySuggestion[], Error>({
    queryKey: ['countries-all'],
    queryFn: fetchAllCountries,
    enabled,
    staleTime: Infinity, // Cache indefinitely
    retry: 2,
    refetchOnWindowFocus: false,
  })

  // Filter countries client-side based on debounced query
  const suggestions = useMemo(() => {
    if (!allCountries || !debouncedQuery || debouncedQuery.length < 2) {
      return allCountries ?? []
    }

    const lowerQuery = debouncedQuery.toLowerCase()
    return allCountries.filter(country =>
      country.label.toLowerCase().includes(lowerQuery)
    )
  }, [allCountries, debouncedQuery])

  return {
    suggestions,
    total: suggestions.length,
    isLoading,
    error,
    updateQuery: setQuery,
    query,
  }
}


